// App.js - Safer Implementation
import "./App.css";
import React, { Suspense, lazy, useEffect } from "react";
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import MainLayout from "./components/MainLayout";
import Dashboard from "./components/Dashboard";
import Authentication from "./components/Authentication";
import Authorization from "./components/Authorization";
import PageNotFound from "./components/PageNotFound";
import Login from "./components/Login";
import ContactInquiryTable from "./components/Admin/Contact/ContactInquiryTable";
import ServicesManagement from "./components/Admin/Service/ServiceManagement";

import About from "./website/components/About";
import Privacy from "./website/components/Privacy";
import ContactUs from "./website/components/ContactUs";
import Signup from "./website/components/SignUp";
import UserLogin from "./website/components/UserLogin";
import Home from "./website/Home";
import Terms from "./website/components/Terms";
import RefundPolicy from "./website/components/RefundPolicy";
import MyOrders from "./website/components/MyOrders";
import OrderDetails from "./website/components/OrderDetails";
import UserProfile from "./website/components/UserProfile";
import TempleHistory from "./website/components/TempleHistory";
import ScrollToTop from "./ScrollToTop";
import OrderManagement from "./components/Admin/Orders/OrderManagement";
import PaymentManagement from "./components/Admin/Payments/PaymentManagement";
// import NotificationManagement from "./components/Admin/Notification/NotificationManagement";
import NotificationsPage from "./website/components/Notifications/NotificationPage";
import EmailVerification from "./website/components/EmailVerification";
import ForgotPassword from "./website/components/ForgetPassword";
import ResetPassword from "./website/components/ResetPassword";
import UserManagement from "./components/Admin/User/UserManagement";
import ServicesGrid from "./website/components/Home/ServicesGrid";
import EventPage from "./website/components/EventPage";
import BookingPage from "./website/components/BookingPage";
import BookingSuccess from "./website/components/BookingSuccess";
import EventManagement from "./components/Admin/Events/EventManagement";
import EventsPage from "./website/components/EventsPage";
import useApi from "./hooks/useApi";
import { API_METHODS } from "./constants";

// Lazy load the Google Translate Widget to avoid loading issues
// const GoogleTranslateWidget = lazy(() =>
//   import("./components/GoogleTranslateWidget")
// );

// Error boundary for Google Translate Widget
// class TranslateErrorBoundary extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = { hasError: false };
//   }

//   static getDerivedStateFromError(error) {
//     return { hasError: true };
//   }

//   componentDidCatch(error, errorInfo) {
//     console.error("Google Translate Error:", error, errorInfo);
//   }

//   render() {
//     if (this.state.hasError) {
//       return (
//         <div
//           style={{
//             padding: "5px 10px",
//             background: "#f8f9fa",
//             border: "1px solid #dee2e6",
//             borderRadius: "4px",
//             fontSize: "12px",
//             color: "#6c757d",
//           }}
//         >
//           Translation unavailable
//         </div>
//       );
//     }

//     return this.props.children;
//   }
// }

// Create a wrapper component for public routes that includes Google Translate
const PublicLayoutWrapper = ({ children }) => {
  return (
    <div>
      {/* Google Translate Widget - positioned at top right with error handling */}
      {/* <div
        style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          zIndex: 1000,
          background: "white",
          padding: "5px 10px",
          borderRadius: "5px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        }}
      >
        <TranslateErrorBoundary>
          <Suspense
            fallback={
              <div style={{ fontSize: "12px", color: "#666" }}>Loading...</div>
            }
          >
            <GoogleTranslateWidget />
          </Suspense>
        </TranslateErrorBoundary>
      </div> */}
      {children}
    </div>
  );
};

// Domain detection
const getSubdomain = () => {
  const hostname = window.location.hostname;

  if (hostname === "admin.temple.local") {
    return "admin";
  }

  if (hostname.startsWith("admin.")) {
    return "admin";
  }

  return null;
};

function App() {
  const [response, request] = useApi("/user/counter", null, API_METHODS.POST);
  const subdomain = getSubdomain();
  const isAdminDomain = subdomain === "admin";
  useEffect(() => {
    if (!isAdminDomain) {
      request();
    }
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {isAdminDomain ? (
          // Admin routes - NO Google Translate
          <>
            <Route path="/login" element={<Login />} />
            <Route element={<Authentication />}>
              <Route path="portal" element={<MainLayout />}>
                <Route element={<Authorization allowedRoles={["1"]} />}>
                  <Route path="/portal/dashboard" element={<Dashboard />} />
                  <Route
                    path="/portal/enquiry"
                    element={<ContactInquiryTable />}
                  />
                  <Route path="/portal/orders" element={<OrderManagement />} />
                  <Route
                    path="/portal/payments"
                    element={<PaymentManagement />}
                  />
                  {/* <Route
                    path="/portal/notifications"
                    element={<NotificationManagement />}
                  /> */}
                  <Route
                    path="/portal/services"
                    element={<ServicesManagement />}
                  />
                  <Route path="/portal/users" element={<UserManagement />} />
                  <Route path="/portal/events" element={<EventManagement />} />
                </Route>
              </Route>
            </Route>
          </>
        ) : (
          // Public website routes - WITH Google Translate
          <>
            <Route
              path="/"
              element={
                <PublicLayoutWrapper>
                  <ScrollToTop />
                </PublicLayoutWrapper>
              }
            >
              <Route index element={<Home />} />
              <Route path="/terms-conditions" element={<Terms />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/about-us" element={<About />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/sign-up" element={<Signup />} />
              <Route path="/login" element={<UserLogin />} />
              <Route path="/services" element={<ServicesGrid />} />
              <Route path="/book/:serviceSlug" element={<BookingPage />} />
              <Route path="/booking/success" element={<BookingSuccess />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/event/:slug" element={<EventPage />} />
              <Route path="/orders" element={<MyOrders />} />
              <Route path="/orders/:orderId" element={<OrderDetails />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/temple" element={<TempleHistory />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route
                path="/verify-email/:token"
                element={<EmailVerification />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
            </Route>
          </>
        )}
        <Route path="*" element={<PageNotFound />} />
      </>
    )
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Collapse: {
            headerBg: "rgb(20, 168, 0)",
            colorTextHeading: "rgb(255, 255, 255)",
          },
        },
        token: {
          algorithm: "compactAlgorithm",
          colorPrimary: "#1677ff",
          borderRadius: 11,
          fontFamily: "Manrope",
          colorTextBase: "#455560",
        },
      }}
    >
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default App;
